import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ExtraValuesMonthlyChart = ({ months, monthLabels, expenses, savings, onBarClick }) => {
  // chart.js callbacks (ticks/tooltip/plugin) run OUTSIDE the React render —
  // formatNumberToCurrency calls useProjectContext internally and would throw
  // "Invalid hook call" there. Capture the context here and format with a pure fn.
  const { cubBased, activeProject } = useProjectContext();
  const indexerValue = activeProject?.indexer?.value;
  const formatValue = (value) => {
    const safe = Number(value) || 0;
    if (cubBased) return formatNumberToLocale(safe / (indexerValue && indexerValue !== 0 ? indexerValue : 1));
    return safe.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const data = {
    labels: monthLabels,
    datasets: [
      { label: "Despesas", data: expenses, backgroundColor: "#f27752" },
      { label: "Economias", data: savings, backgroundColor: "#3CB371" },
    ],
  };

  // Draws each bar's value just outside the bar (R$ formatted), above for
  // expenses and below for savings. Inline plugin → no extra dependency.
  const valueLabelsPlugin = {
    id: "extraValuesBarLabels",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;
      ctx.save();
      ctx.font = "600 10px sans-serif";
      ctx.textAlign = "center";
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        meta.data.forEach((bar, index) => {
          const raw = dataset.data[index];
          if (!raw) return;
          ctx.fillStyle = dataset.backgroundColor;
          if (raw >= 0) {
            ctx.textBaseline = "bottom";
            ctx.fillText(formatValue(raw), bar.x, bar.y - 4);
          } else {
            ctx.textBaseline = "top";
            ctx.fillText(formatValue(raw), bar.x, bar.y + 4);
          }
        });
      });
      ctx.restore();
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 20, bottom: 12 } },
    // 'index' + intersect:false → clicar em qualquer ponto da coluna do mês
    // (mesmo fora da barra) já resolve o mês.
    interaction: { mode: "index", intersect: false },
    onClick: (event, elements, chart) => {
      if (!onBarClick) return;
      let index = elements?.[0]?.index;
      if (index == null) {
        const points = chart.getElementsAtEventForMode(event, "index", { intersect: false }, true);
        index = points?.[0]?.index;
      }
      const monthKey = index != null ? months[index] : null;
      if (monthKey) onBarClick(monthKey);
    },
    onHover: (event) => {
      if (event?.native?.target) event.native.target.style.cursor = onBarClick ? "pointer" : "default";
    },
    plugins: {
      // A plataforma registra chartjs-plugin-datalabels globalmente, que
      // desenha o valor cru dentro da barra. Desligamos aqui — usamos só o
      // rótulo R$ no topo (valueLabelsPlugin).
      datalabels: { display: false },
      legend: { position: "bottom" },
      tooltip: {
        callbacks: {
          label: (tooltipItem) => `${tooltipItem.dataset.label}: ${formatValue(tooltipItem.raw)}`,
          footer: () => (onBarClick ? "Clique para ver os lançamentos do mês" : ""),
        },
      },
    },
    scales: {
      y: { ticks: { callback: (value) => formatValue(value) } },
    },
  };

  // Com muitos meses o gráfico rola horizontalmente (uma coluna por mês precisa
  // de largura mínima p/ o rótulo R$ não sobrepor); com poucos, preenche 100%.
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ height: "340px", minWidth: "100%", width: `${Math.max(monthLabels.length * 90, 320)}px` }}>
        <Bar data={data} options={options} plugins={[valueLabelsPlugin]} />
      </div>
    </div>
  );
};

export default ExtraValuesMonthlyChart;
